/**
 * EgresosView -- Lista de egresos (#/egresos) con paridad Desktop FrmEgresosV15.
 *
 * Filtros:
 *   - PERIODO   (Total / Mes / Semana / Hoy / Rango)
 *   - ESTADO    (todos / pagada / pendiente / anulada)
 *   - FECHA INI / FECHA FIN (rango)
 *   - CONCEPTO  (filtro contextual por concepto del egreso)
 *   - METODO    (efectivo / transferencia / tarjeta / otro)
 *
 * Lista:
 *   - Tabla columnas: Fecha / Hora / Concepto / Metodo / Estado / Usuario / Total
 *   - Click row: expande detalle (factura asociada si aplica + items)
 *   - Totales: total egresos, total anulado, neto
 *
 * @module presentation/views/EgresosView
 */

import { FinanzasApiClient } from '../../infrastructure/api/FinanzasApiClient.js';
import { AuditoriaApiClient } from '../../infrastructure/api/AuditoriaApiClient.js';
import { getSelectedBusiness } from '../components/BusinessSelector.js';
import { MARCAS, leerMarca, apiTraeMarca, cuerpoMarca, mensajeMarca } from '../../domain/marcaEgreso.js';

const PERIODS = [
  { code: 'total',  label: 'Total' },
  { code: 'mes',    label: 'Mes' },
  { code: 'semana', label: 'Semana' },
  { code: 'hoy',    label: 'Hoy' },
  { code: 'rango',  label: 'Rango' }
];
const ESTADOS = [
  { code: '',          label: 'Todos' },
  { code: 'pagada',    label: 'Pagada' },
  { code: 'pendiente', label: 'Pendiente' },
  { code: 'anulada',   label: 'Anulada' }
];
// EGP-T2: valores con CASING EXACTO de la BD (FrmEgresosV15) — el filtro backend
// es exact-match (metodo = @metodo). Los lowercase previos (efectivo/transferencia/
// tarjeta/otro) NUNCA matcheaban la data real (metodo='Caja'/'Externo') -> filtro
// fantasma. 'Externo' habilita ver los 18 externos ($9.06M) directo (ASSIGN #2183).
const METODOS = [
  { code: '',        label: 'Todos' },
  { code: 'Caja',    label: 'Caja (efectivo)' },
  { code: 'Externo', label: 'Externo' }
];
const PAGE_SIZE = 100;

const ESTADO_COLOR = {
  pagada:    '#3fb950',
  pendiente: '#d29922',
  anulada:   '#f85149'
};

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc as escHtml } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

// BAN-T2: el egreso marcado lo dice en la lista, junto al concepto (normal = nada).
const chipMarca = (marca) => {
  const m = MARCAS.find((x) => x.valor === marca);
  return marca && m ? ` <span data-ev-chip-marca="${marca}" style="margin-left:6px;background:${m.color}22;color:${m.color};padding:1px 7px;border-radius:9px;font-size:0.875rem;font-weight:600">${m.texto}</span>` : '';
};

const formatCurrency = (n) => {
  const num = Number(n) || 0;
  return num.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// Paridad Ingresos<->Egresos (REDIRECT Josse #2157):
// fecha = solo YYYY-MM-DD (la hora vive en su propia columna).
const formatFecha = (f) => (f ? String(f).slice(0, 10) : '');
// hora 24h ("19:10:00.6" | "19:10") -> AM/PM es-CO ("7:10 p. m."). Medianoche
// exacta o sin dato -> "—" (data legacy date-only no trae hora real).
const formatHora = (h) => {
  if (!h) return '—';
  const m = String(h).match(/(\d{1,2}):(\d{2})/);
  if (!m) return '—';
  let hh = parseInt(m[1], 10);
  const mm = m[2];
  if (hh === 0 && mm === '00') return '—';
  const ampm = hh >= 12 ? 'p. m.' : 'a. m.';
  hh = hh % 12; if (hh === 0) hh = 12;
  return `${hh}:${mm} ${ampm}`;
};

export async function renderEgresos(container) {
  const state = {
    items: [],
    marcaMsg: null, // BAN-T2: lo que se le dijo al dueno tras marcar el egreso abierto
    // EGP-T1: el backend devuelve totales={ingresoTotal,egresoTotal,saldoNeto}
    // (BusinessFinanzasTotalesSimpleDto), NO totalEgresos. Guardamos egresoTotal.
    totales: { egresoTotal: 0 },
    externoTotal: null, // SUM metodo Externo (2da llamada; backend no expone porMedio)
    // A-T4 (PANEL-FINANZAS-INGRESOS-EGRESOS-PARIDAD-V1-001): default 'total' (no 'mes').
    // La data real de egresos/compras es de mayo; con default 'mes'=junio la vista salia
    // vacia. 'total' muestra todo el historico desde el primer arranque.
    filters: { period: 'total', estado: '', from: '', to: '', concepto: '', metodo: '', rubroId: '' },
    rubros: [], // RB-T3: catalogo para el dropdown (GET /rubros-egreso)
    totalCount: 0,
    page: 1, // EGP-T2: paginacion (antes pedia pageSize=20 sin UI -> los 18 externos quedaban fuera de la 1ra pagina)
    expandedId: null,
    expandedDetail: null
  };

  // RB-T3 (contrato #2223): cargar catalogo de rubros para el dropdown ANTES del
  // primer render de filtros. Read-only; degrada a [] si el endpoint no esta live.
  {
    const bizId = getSelectedBusiness();
    if (bizId) {
      const rres = await FinanzasApiClient.listRubrosEgreso(bizId);
      if (rres.ok) state.rubros = rres.items;
    }
  }

  container.innerHTML = `
    <section class="artifact-view egresos-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>💸 Egresos</h1>
          <p style="margin:4px 0 0;color:#8b949e;font-size:0.875rem">
            Salidas de caja registradas — paridad Desktop FrmEgresosV15
          </p>
        </div>
        <div data-ev-poll style="font-size:0.875rem;color:#8b949e">⟳ cargando…</div>
      </div>

      <form data-ev-filters style="display:flex;flex-wrap:wrap;gap:10px;align-items:flex-end;margin-top:14px;
                                   padding:12px;background:#161b22;border:1px solid #30363d;border-radius:8px">
        ${renderFilterFields(state.filters, state.rubros)}
        <button type="submit" class="btn-primary btn-sm">Aplicar</button>
        <button type="button" data-ev-reset class="btn-ghost btn-sm">Limpiar</button>
      </form>

      <div data-ev-totales style="display:flex;gap:14px;margin-top:14px;flex-wrap:wrap"></div>

      <div style="margin-top:14px;border:1px solid #30363d;border-radius:8px;overflow:hidden;background:#0d1117">
        <table style="width:100%;border-collapse:collapse;font-size:0.875rem">
          <thead style="background:#161b22">
            <tr>
              <th style="padding:8px 10px;text-align:left;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">Fecha</th>
              <th style="padding:8px 10px;text-align:left;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">Hora</th>
              <th style="padding:8px 10px;text-align:left;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">Concepto</th>
              <th style="padding:8px 10px;text-align:left;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">Metodo</th>
              <th style="padding:8px 10px;text-align:left;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">Rubro</th>
              <th style="padding:8px 10px;text-align:left;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">Estado</th>
              <th style="padding:8px 10px;text-align:left;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">Usuario</th>
              <th style="padding:8px 10px;text-align:right;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">Total</th>
            </tr>
          </thead>
          <tbody data-ev-tbody></tbody>
        </table>
      </div>

      <div data-ev-pager style="display:flex;gap:10px;align-items:center;justify-content:flex-end;margin-top:10px"></div>
    </section>
  `;

  const $form = container.querySelector('[data-ev-filters]');
  const $reset = container.querySelector('[data-ev-reset]');
  const $tbody = container.querySelector('[data-ev-tbody]');
  const $totales = container.querySelector('[data-ev-totales]');
  const $poll = container.querySelector('[data-ev-poll]');
  const $pager = container.querySelector('[data-ev-pager]');

  $form.addEventListener('change', (e) => {
    if (e.target.name === 'period') updatePeriodVisibility(container, e.target.value);
  });
  $form.addEventListener('submit', (e) => {
    e.preventDefault();
    Object.assign(state.filters, readFilters(container));
    state.page = 1;
    loadList();
  });
  $reset.addEventListener('click', () => {
    state.filters = { period: 'total', estado: '', from: '', to: '', concepto: '', metodo: '', rubroId: '' };
    state.page = 1;
    container.querySelector('[data-ev-filters]').reset();
    container.querySelectorAll('[name="period"]').forEach(el => { el.value = 'total'; });
    updatePeriodVisibility(container, 'total');
    loadList();
  });

  await loadList();

  async function loadList() {
    const businessId = getSelectedBusiness();
    if (!businessId) {
      $tbody.innerHTML = `<tr><td colspan="8" style="padding:20px;text-align:center;color:#d29922">Selecciona un negocio en la barra superior</td></tr>`;
      $totales.innerHTML = '';
      $poll.textContent = '⚠ sin negocio';
      return;
    }
    $poll.textContent = '⟳ cargando…';
    const res = await FinanzasApiClient.listEgresos(businessId, { ...state.filters, page: state.page, pageSize: PAGE_SIZE });
    if (!res.ok) {
      $tbody.innerHTML = `<tr><td colspan="8" style="padding:20px;text-align:center;color:#f85149">Error: ${escHtml(JSON.stringify(res.error || {}).slice(0, 120))}</td></tr>`;
      $poll.textContent = '⚠ error';
      return;
    }
    const payload = res.data?.data ?? res.data ?? {};
    state.items = Array.isArray(payload.items) ? payload.items : [];
    state.totales = payload.totales || calcTotales(state.items);
    state.totalCount = Number(payload.totalCount) || state.items.length;
    renderRows();
    renderTotales(payload._hint);
    renderPager();
    $poll.textContent = `✓ ${state.totalCount} egreso(s) · ${formatearFecha(new Date(), {}, { parte: 'hora' })}`;
    // EGP-T1: card Externo via 2da llamada (el backend no expone porMedio para
    // egresos; totales.egresoTotal es sobre TODO el set filtrado, no paginado, asi
    // que metodo=Externo + pageSize=1 da el SUM real sin traer los items). Async
    // no-bloqueante: la tabla ya pinto; el card se rellena al volver.
    loadExternoTotal(businessId);
  }

  async function loadExternoTotal(businessId) {
    state.externoTotal = null;
    // 'Externo' = valor canonico de FrmEgresosV15 (Desktop). El filtro backend es
    // exact-match; si el casing del Desktop cambiara, el card mostraria $0 (fail
    // VISIBLE, no silencioso) y seria señal de revisar el uploader.
    const res = await FinanzasApiClient.listEgresos(businessId, { ...state.filters, metodo: 'Externo', page: 1, pageSize: 1 });
    if (res.ok) {
      const p = res.data?.data ?? res.data ?? {};
      state.externoTotal = Number(p.totales?.egresoTotal ?? p.totales?.totalEgresos ?? 0);
    }
    renderTotales();
  }

  function renderRows() {
    if (state.items.length === 0) {
      $tbody.innerHTML = `<tr><td colspan="8" style="padding:24px;text-align:center;color:#8b949e">
        Sin egresos para los filtros aplicados.<br/>
        <small style="color:#6e7681">Si el endpoint /api/v1/businesses/{id}/egresos aun no esta listo (T2.0 Argy-API), esta vista quedara vacia hasta que se entregue.</small>
      </td></tr>`;
      return;
    }
    $tbody.innerHTML = state.items.map(it => renderRow(it)).join('');
    $tbody.querySelectorAll('[data-ev-row-id]').forEach(tr => {
      tr.addEventListener('click', async () => {
        const id = tr.dataset.evRowId;
        if (state.expandedId === id) {
          state.expandedId = null;
          state.expandedDetail = null;
          renderRows();
          return;
        }
        state.expandedId = id;
        state.marcaMsg = null;
        state.expandedDetail = { loading: true };
        renderRows();
        const businessId = getSelectedBusiness();
        const res = await FinanzasApiClient.getEgresoById(businessId, id);
        state.expandedDetail = res.ok
          ? (res.data?.data ?? res.data ?? null)
          : { error: res.error };
        renderRows();
      });
    });
    $tbody.querySelectorAll('[data-ev-marca]').forEach((b) => {
      b.addEventListener('click', (e) => { e.stopPropagation(); marcar(b.dataset.evMarca || null); });
    });
  }

  function renderRow(it) {
    const isExpanded = state.expandedId === it.id;
    const estado = (it.estado || 'pagada').toLowerCase();
    const estadoColor = ESTADO_COLOR[estado] || '#8b949e';
    const total = formatCurrency(it.total);
    // EGP-T1: el DTO C# (BusinessEgresoItemDto) usa snake_case fecha_operativa/
    // hora_operativa. El panel leia camelCase fechaOperativa/horaOperativa (nunca
    // presente) -> columnas vacias. Lectura snake-first + fallbacks defensivos.
    // REDIRECT #2157: fecha sin hora pegada + hora AM/PM (egresos SI trae hora real).
    const fecha = formatFecha(it.fecha_operativa || it.fechaOperativa || it.fecha);
    const hora = formatHora(it.hora_operativa || it.horaOperativa || it.hora);
    return `
      <tr data-ev-row-id="${escHtml(it.id)}"
          style="cursor:pointer;border-bottom:1px solid #21262d;background:${isExpanded ? '#1c2530' : 'transparent'}"
          onmouseover="this.style.background='${isExpanded ? '#1c2530' : '#161b22'}'"
          onmouseout="this.style.background='${isExpanded ? '#1c2530' : 'transparent'}'">
        <td style="padding:8px 10px;color:#c9d1d9">${escHtml(fecha)}</td>
        <td style="padding:8px 10px;color:#c9d1d9">${escHtml(hora)}</td>
        <td style="padding:8px 10px;color:#c9d1d9">${escHtml(it.concepto || '-')}${chipMarca(leerMarca(it))}</td>
        <td style="padding:8px 10px;color:#c9d1d9">${escHtml(it.metodo || '-')}</td>
        <td style="padding:8px 10px;color:#c9d1d9">${it.rubro_nombre ? escHtml(it.rubro_nombre) : '<span style="color:#6e7681">—</span>'}</td>
        <td style="padding:8px 10px"><span style="background:${estadoColor}22;color:${estadoColor};padding:2px 8px;border-radius:10px;font-size:0.875rem;font-weight:600">${escHtml(estado)}</span></td>
        <td style="padding:8px 10px;color:#c9d1d9">${escHtml(it.usuario || '-')}</td>
        <td style="padding:8px 10px;text-align:right;color:#f85149;font-weight:600">$${total}</td>
      </tr>
      ${isExpanded ? `<tr><td colspan="8" style="padding:0;background:#0d1117;border-bottom:1px solid #21262d">
        ${renderDetail(state.expandedDetail)}
      </td></tr>` : ''}
    `;
  }

  // BAN-T2 (D-64): la marca gerencial/personal del egreso. Solo el dueno la cambia (la API responde 403 a los demas y se
  // dice). Si la API de este ambiente aun no trae la marca, se dice y no se ofrece el selector.
  function bloqueMarca(detail) {
    if (!apiTraeMarca(detail)) {
      return '<div data-ev-marca-falta style="margin:0 0 10px;font-size:0.875rem;color:#6e7681">La marca de pago gerencial o personal aparece aquí cuando la API la publique.</div>';
    }
    const actual = leerMarca(detail);
    const msg = state.marcaMsg;
    return `
      <div data-ev-marca-bloque style="margin:0 0 12px;display:flex;gap:8px;align-items:center;flex-wrap:wrap;font-size:0.875rem">
        <strong style="color:#8b949e">Marca:</strong>
        ${MARCAS.map((m) => {
          const on = (m.valor ?? null) === actual;
          return `<button type="button" data-ev-marca="${m.valor ?? ''}" aria-pressed="${on}"
            style="min-height:32px;padding:4px 12px;border-radius:14px;cursor:pointer;font-size:0.875rem;border:1px solid ${on ? m.color : '#30363d'};background:${on ? m.color + '22' : '#161b22'};color:${on ? m.color : '#c9d1d9'}">${m.texto}</button>`;
        }).join('')}
        ${msg ? `<span data-ev-marca-msg style="color:${msg.ok ? '#3fb950' : '#d29922'}">${escHtml(msg.texto)}</span>` : ''}
      </div>`;
  }

  async function marcar(valor) {
    const businessId = getSelectedBusiness();
    const id = state.expandedId;
    if (!id) return;
    const res = await AuditoriaApiClient.marcarEgreso(businessId, id, cuerpoMarca(valor));
    state.marcaMsg = mensajeMarca(res, valor);
    if (res.ok) {
      if (state.expandedDetail) state.expandedDetail.marca_privada = valor;
      const fila = state.items.find((x) => x.id === id);
      if (fila) fila.marca_privada = valor;
    }
    renderRows();
  }

  function renderDetail(detail) {
    if (!detail) return '<div style="padding:14px;color:#8b949e">Cargando…</div>';
    if (detail.loading) return '<div style="padding:14px;color:#8b949e">⟳ Cargando detalle…</div>';
    if (detail.error) return `<div style="padding:14px;color:#f85149">Error: ${escHtml(JSON.stringify(detail.error).slice(0, 200))}</div>`;
    const items = Array.isArray(detail.items) ? detail.items : [];
    return `
      <div style="padding:14px">
        <h4 style="margin:0 0 10px;color:#f0f6fc;font-size:0.9rem">📋 Detalle del egreso</h4>
        <div style="display:flex;gap:14px;flex-wrap:wrap;margin-bottom:10px;font-size:0.875rem;color:#c9d1d9">
          <span><strong style="color:#8b949e">Concepto:</strong> ${escHtml(detail.concepto || '-')}</span>
          <span><strong style="color:#8b949e">Metodo:</strong> ${escHtml(detail.metodo || '-')}</span>
          <span><strong style="color:#8b949e">Pagada:</strong> ${detail.pagada ? 'si' : 'no'}</span>
          ${detail.facturaId ? `<span><strong style="color:#8b949e">Factura:</strong> <code>${escHtml(detail.facturaId)}</code></span>` : ''}
        </div>
        ${bloqueMarca(detail)}
        ${items.length === 0 ? '<div style="color:#6e7681;font-size:0.875rem">Sin items detallados</div>' : `
          <table style="width:100%;border-collapse:collapse;font-size:0.875rem">
            <thead><tr>
              <th style="padding:5px 8px;text-align:left;color:#8b949e;border-bottom:1px solid #30363d">Item</th>
              <th style="padding:5px 8px;text-align:right;color:#8b949e;border-bottom:1px solid #30363d">Cant.</th>
              <th style="padding:5px 8px;text-align:right;color:#8b949e;border-bottom:1px solid #30363d">Precio</th>
              <th style="padding:5px 8px;text-align:right;color:#8b949e;border-bottom:1px solid #30363d">Subtotal</th>
            </tr></thead>
            <tbody>
              ${items.map(i => `
                <tr>
                  <td style="padding:5px 8px;color:#c9d1d9">${escHtml(i.descripcion || i.nombre || i.producto || '-')}</td>
                  <td style="padding:5px 8px;text-align:right;color:#c9d1d9">${escHtml(i.cantidad || 1)}</td>
                  <td style="padding:5px 8px;text-align:right;color:#c9d1d9">$${formatCurrency(i.precio || i.precioUnitario)}</td>
                  <td style="padding:5px 8px;text-align:right;color:#f85149;font-weight:600">$${formatCurrency(i.subtotal || i.total)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `}
      </div>
    `;
  }

  function renderPager() {
    const totalPages = Math.max(1, Math.ceil(state.totalCount / PAGE_SIZE));
    if (totalPages <= 1) { $pager.innerHTML = ''; return; }
    $pager.innerHTML = `
      <button type="button" data-ev-prev class="btn-ghost btn-sm" ${state.page <= 1 ? 'disabled' : ''}>&lsaquo; Anterior</button>
      <span style="font-size:0.875rem;color:#8b949e">Página ${state.page} de ${totalPages}</span>
      <button type="button" data-ev-next class="btn-ghost btn-sm" ${state.page >= totalPages ? 'disabled' : ''}>Siguiente &rsaquo;</button>
    `;
    $pager.querySelector('[data-ev-prev]')?.addEventListener('click', () => {
      if (state.page > 1) { state.page--; state.expandedId = null; state.expandedDetail = null; loadList(); }
    });
    $pager.querySelector('[data-ev-next]')?.addEventListener('click', () => {
      if (state.page < totalPages) { state.page++; state.expandedId = null; state.expandedDetail = null; loadList(); }
    });
  }

  function renderTotales(hint) {
    if (hint !== undefined) state._hint = hint; // persistir entre la 1ra y 2da llamada
    // EGP-T1: total real desde el backend (egresoTotal sobre todo el set, fix del $0).
    const total = Number(state.totales.egresoTotal ?? state.totales.totalEgresos ?? state.totales.total ?? 0);
    const externo = state.externoTotal; // null = aun cargando 2da llamada
    // Caja (interno) = lo que salio de caja fisica = Total - Externo. Derivado, sin
    // llamada extra. Caja + Externo = Total (base de conciliacion vs V1.0).
    const caja = externo != null ? total - externo : null;
    const card = (label, value, color, sub) => `
      <div style="flex:1;min-width:160px;padding:12px;background:#161b22;border:1px solid #30363d;border-left:3px solid ${color};border-radius:6px">
        <div style="font-size:0.875rem;color:#8b949e;text-transform:uppercase">${escHtml(label)}</div>
        <div style="font-size:1.2rem;color:${color};font-weight:700;margin-top:4px">${value == null ? '<span style="color:#6e7681;font-size:0.9rem">⟳ …</span>' : '$' + formatCurrency(value)}</div>
        ${sub ? `<div style="font-size:0.875rem;color:#6e7681;margin-top:2px">${escHtml(sub)}</div>` : ''}
      </div>`;
    $totales.innerHTML = `
      ${card('Total Egresos', total, '#f85149')}
      ${card('Caja (interno)', caja, '#3fb950', 'efectivo / caja física')}
      ${card('Externo', externo, '#58a6ff', 'pagos por fuera de caja')}
      ${state._hint ? `<div style="flex:1;min-width:240px;padding:10px;background:#1f6feb22;border:1px dashed #1f6feb;border-radius:6px;color:#79c0ff;font-size:0.875rem">ℹ ${escHtml(state._hint)}</div>` : ''}
    `;
  }
}

// ============== Helpers ==============

function renderFilterFields(filters, rubros = []) {
  const periodOpts = PERIODS.map(p => `<option value="${p.code}" ${p.code === filters.period ? 'selected' : ''}>${p.label}</option>`).join('');
  const estadoOpts = ESTADOS.map(s => `<option value="${s.code}" ${s.code === filters.estado ? 'selected' : ''}>${s.label}</option>`).join('');
  const metodoOpts = METODOS.map(m => `<option value="${m.code}" ${m.code === filters.metodo ? 'selected' : ''}>${m.label}</option>`).join('');
  // RB-T3: dropdown Rubro poblado del catalogo (GET /rubros-egreso). Solo se
  // muestra si el negocio tiene rubros; si no, se omite (sin UI vacia).
  const rubroOpts = `<option value="">Todos</option>` + rubros.map(r =>
    `<option value="${escHtml(r.id)}" ${String(filters.rubroId) === String(r.id) ? 'selected' : ''}>${escHtml(r.nombre)}</option>`).join('');
  const rangoVisible = filters.period === 'rango';
  return `
    <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
      Periodo
      <select name="period" class="form-input form-input-sm" style="min-width:120px">${periodOpts}</select>
    </label>
    <label data-ev-rango-from style="display:${rangoVisible ? 'flex' : 'none'};flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
      Fecha ini
      <input type="date" name="from" class="form-input form-input-sm" value="${filters.from || ''}"/>
    </label>
    <label data-ev-rango-to style="display:${rangoVisible ? 'flex' : 'none'};flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
      Fecha fin
      <input type="date" name="to" class="form-input form-input-sm" value="${filters.to || ''}"/>
    </label>
    <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
      Estado
      <select name="estado" class="form-input form-input-sm" style="min-width:130px">${estadoOpts}</select>
    </label>
    <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
      Concepto
      <input type="text" name="concepto" class="form-input form-input-sm" placeholder="contiene…" value="${filters.concepto || ''}"/>
    </label>
    <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
      Metodo
      <select name="metodo" class="form-input form-input-sm" style="min-width:130px">${metodoOpts}</select>
    </label>
    ${rubros.length > 0 ? `
    <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
      Rubro
      <select name="rubroId" class="form-input form-input-sm" style="min-width:140px">${rubroOpts}</select>
    </label>` : ''}
  `;
}

function readFilters(container) {
  const $form = container.querySelector('[data-ev-filters]');
  return {
    period:   $form.querySelector('[name="period"]')?.value || 'mes',
    estado:   $form.querySelector('[name="estado"]')?.value || '',
    from:     $form.querySelector('[name="from"]')?.value || '',
    to:       $form.querySelector('[name="to"]')?.value || '',
    concepto: $form.querySelector('[name="concepto"]')?.value || '',
    metodo:   $form.querySelector('[name="metodo"]')?.value || '',
    rubroId:  $form.querySelector('[name="rubroId"]')?.value || ''
  };
}

function updatePeriodVisibility(container, period) {
  const isRango = period === 'rango';
  const $from = container.querySelector('[data-ev-rango-from]');
  const $to = container.querySelector('[data-ev-rango-to]');
  if ($from) $from.style.display = isRango ? 'flex' : 'none';
  if ($to) $to.style.display = isRango ? 'flex' : 'none';
}

// Fallback si el backend no manda `totales` (no deberia con el endpoint live):
// suma los items de la pagina actual. Devuelve el shape del backend (egresoTotal).
function calcTotales(items) {
  const egresoTotal = items.reduce((s, it) => s + (Number(it.total) || 0), 0);
  return { egresoTotal };
}
